import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { MotorsDraftFacade, RouterFacade } from '@classifieds-sell/state';
import { FormHelperService } from '@classifieds-sell/shared';
import { combineLatest, Subject } from 'rxjs';
import { MotorsSectionBase } from '../motors-section-base';
import { AuctionTypeEnum, SellingTypeEnum, CalculatedPackage } from '@classifieds-sell/datalayer';
import { getListingPrice } from '../../helpers/price.helper';
import { FeeCalculationFacade } from '../../state/fee-calculation/fee-calculation.facade';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'motors-listing-details-package-section',
    templateUrl: './motors-listing-details-package-section.component.html',
    styleUrls: ['./motors-listing-details-package-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MotorsListingDetailsPackageSectionComponent
    extends MotorsSectionBase
    implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    SellingTypeEnum = SellingTypeEnum;

    @ViewChild('motorsListingDetailsPackageSectionForm')
    motorsListingDetailsPackageSectionForm: NgForm;

    constructor(
        public draftFacade: MotorsDraftFacade,
        private _changeDetectorRef: ChangeDetectorRef,
        private _formHelperService: FormHelperService,
        private feeCalculationFacade: FeeCalculationFacade,
        private routerFacade: RouterFacade
    ) {
        super(draftFacade);
    }

    listingPrice: number | null;
    categoryPackageInvalid = false;
    showPackages = false;
    showModal: boolean;
    selectedPackage: any;
    selectedPackageName: string;
    packageList: any[];
    packageNameList: any[];
    zeroChargeMessage: string;

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        this.packageList = [];
        this.packageNameList = [];
        this.showModal = false;
        this.selectedPackage = null;

        super.onInit(() => {
            this.showPackages =
                (this.draftData.listingDetails.sellingType === SellingTypeEnum.classified && this.draftData.listingDetails.askingPrice) ||
                (this.draftData.listingDetails.sellingType === SellingTypeEnum.auction &&
                    this.draftData.listingDetails.auctionType === AuctionTypeEnum.auctionAndBuyNow &&
                    (this.draftData.listingDetails.reservePrice !== null || this.draftData.listingDetails.startPrice !== null)) ||
                (this.draftData.listingDetails.sellingType === SellingTypeEnum.auction &&
                    this.draftData.listingDetails.auctionType === AuctionTypeEnum.buyNowOnly &&
                    this.draftData.listingDetails.buyNowPrice !== null);

            this.listingPrice = getListingPrice(
                this.categoryId,
                this.draftData
            );

            this._changeDetectorRef.markForCheck();
        });
        combineLatest([
            this.feeCalculationFacade.packagePrices$,
            this.routerFacade.routerData$
        ])
            .pipe(takeUntil(this._destroyed$))
            .subscribe(([packagePrices, { editing, relisting }]) => {
                this.packageList = [];
                this.packageNameList = [];

                if (editing) {
                    this.zeroChargeMessage = 'Current package';
                } else if (relisting) {
                    this.zeroChargeMessage = 'Free';
                }

                if (this.showPackages) {
                    packagePrices.forEach((pack: CalculatedPackage) => {
                        if (pack.sellingType === this.draftData.listingDetails.sellingType) {
                            const photos = JSON.parse(
                                pack.packageUi.photos
                            );

                            const photoUrl = photos.images[0];
                            const price = pack.packagePrice;
                            const features = JSON.parse(pack.packageUi.features).Features;

                            this.packageList.push({
                                id: pack.id,
                                name: pack.name,
                                packageUi: pack.packageUi,
                                features: features.map((feature: any) => {
                                    return {
                                        description: feature.Description,
                                        checked: feature.Checked === 'true'
                                    };
                                }),
                                photo: photoUrl,
                                price: price,
                                isDisabled: pack.isDisabled
                            });

                            this.packageNameList.push(pack.name);
                        }
                    });
                }

                this.packageNameList = this.packageNameList.slice().reverse();
            });
    }

    onSelectPackage(): void {
        this.categoryPackageInvalid = false;
        this.publish();
    }

    isValid(): boolean {
        this._formHelperService.validate(this.motorsListingDetailsPackageSectionForm);
        this.categoryPackageInvalid = this.draftData.listingDetails.categoryPackageId === null;
        this._changeDetectorRef.markForCheck();

        return this.motorsListingDetailsPackageSectionForm.form.valid && !this.categoryPackageInvalid;
    }

    dirty(): boolean {
        return this.motorsListingDetailsPackageSectionForm.form.dirty;
    }

    openModal(packageId: number): void {
        this.selectedPackage = this.packageList.find((i) => i.id === packageId);
        this.selectedPackageName = this.selectedPackage.name;
        this.showModal = true;
    }

    setSelectedPackage(packageName: any){
        this.selectedPackage = this.packageList.find((i) => i.name === packageName.value);
    }

    closeModal(): void {
        this.showModal = false;
    }
}
